import {useState,useSyncExternalStore} from 'react';
import {Link} from 'react-router';
import type {ProductCardMode,SmtPresentationPreferences} from '../presentation-preferences.ts';
import type {CarrierOtaPort} from '../carrier-ota-port.ts';
import type {AndroidRuntimeIdentity} from '../smt-device-gate-bootstrap-consumer.ts';
import {OtaControlSheet} from '../components/OtaControlSheet.tsx';

const cardModes: readonly {id: ProductCardMode; label: string; detail: string}[] = [
  {id: 'large', label: '大圖卡', detail: '圖片優先，適合熟悉商品圖片'},
  {id: 'compact', label: '緊湊卡', detail: '同屏顯示更多商品，保留圖片'},
  {id: 'text', label: '純文字', detail: '最快掃名同價錢，圖片收起'},
];

type MoreModule={
  readonly title:string;
  readonly detail:string;
  readonly owner:string;
  readonly href?:string;
  readonly liveLabel?:string;
};

const modules:readonly MoreModule[] = [
  {title: '今日營運', detail: '接單狀態、ETA、提早收工', owner: 'C · A5', href:'/business-day',liveLabel:'已接 C REQ-11 →'},
  {title: '交更／日結', detail: 'Cash Shift、Cash In/Out、Day Close', owner: 'C · A5', href:'/business-day/shift',liveLabel:'已接 C REQ-11 →'},
  {title: '報表', detail: '今日營運、產品、付款、現金', owner: 'C · A6', href:'/reports',liveLabel:'已接 C REQ-12 →'},
  {title: '設備／打印', detail: '訂單打印狀態、補印、failed 重試；設備健康仍由 A', owner: 'C Print + A evidence', href:'/print-ops',liveLabel:'已接 C REQ-13 →'},
];

const otaLabel=(phase:ReturnType<CarrierOtaPort['getSnapshot']>['phase'])=>{
  if(phase==='available')return '發現新版本 →';
  if(phase==='staged')return '已下載 · 等待安裝 →';
  if(phase==='checking')return '正在檢查…';
  if(phase==='downloading')return '正在下載驗證…';
  if(phase==='up-to-date')return '已是最新版本 →';
  if(phase==='failed')return '更新操作有錯誤 →';
  if(phase==='unavailable')return '只限 SMT 實機';
  return '手動控制 →';
};

export function MoreHub({
  preferences,
  onProductCardModeChange,
  otaPort,
  runtimeIdentity,
}: {
  preferences:SmtPresentationPreferences;
  onProductCardModeChange:(mode:ProductCardMode)=>void;
  otaPort:CarrierOtaPort;
  runtimeIdentity:AndroidRuntimeIdentity|null;
}) {
  const [otaOpen,setOtaOpen]=useState(false);
  const ota=useSyncExternalStore(otaPort.subscribe,otaPort.getSnapshot,otaPort.getSnapshot);

  return <main className="more-hub" aria-label="更多">
    <section className="more-hero panel">
      <div><span className="eyebrow">SMT 控制中心</span><h1>更多</h1><p>B 只管理畫面；營運由 C、裝置／OTA 由 A 線權威介面執行。</p></div>
      <span className="more-live-badge">V1 主權已鎖定</span>
    </section>

    <section className="more-section panel" aria-labelledby="display-settings-title">
      <div className="more-section-head"><div><span className="eyebrow">B 線｜本機顯示</span><h2 id="display-settings-title">商品卡顯示</h2></div><small>即時套用到點單頁</small></div>
      <div className="presentation-choice-grid">
        {cardModes.map(mode=><button key={mode.id} aria-pressed={preferences.productCardMode===mode.id} className={preferences.productCardMode===mode.id?'selected':''} onClick={()=>onProductCardModeChange(mode.id)}>
          <span className={`card-mode-preview mode-${mode.id}`} aria-hidden="true"><i/><i/><i/></span>
          <b>{mode.label}</b><small>{mode.detail}</small>
        </button>)}
      </div>
      <p className="more-preference-note">只保存畫面顯示方式；唔會保存價錢、訂單、售罄、付款或任何營運狀態。</p>
    </section>

    <section className="more-section panel" aria-labelledby="future-modules-title">
      <div className="more-section-head"><div><span className="eyebrow">Control Center</span><h2 id="future-modules-title">營運／裝置模組</h2></div><small>逐個權威介面接通</small></div>
      <div className="more-module-grid">
        <button type="button" className={`more-module live ota-module phase-${ota.phase}`} onClick={()=>setOtaOpen(true)}>
          <div><b>系統更新</b><p>檢查、下載、安裝／啟用、回復上一版本</p></div>
          <span>A · Runtime OTA</span><small>{otaLabel(ota.phase)}</small>
        </button>
        {modules.map(module=>module.href?<Link className="more-module live" key={module.title} to={module.href}><div><b>{module.title}</b><p>{module.detail}</p></div><span>{module.owner}</span><small>{module.liveLabel}</small></Link>:<article className="more-module locked" key={module.title}><div><b>{module.title}</b><p>{module.detail}</p></div><span>{module.owner}</span><small>待接通</small></article>)}
      </div>
    </section>

    <OtaControlSheet open={otaOpen} port={otaPort} runtimeIdentity={runtimeIdentity} onClose={()=>setOtaOpen(false)}/>
  </main>;
}